import { Component, OnDestroy, OnInit } from '@angular/core';
import {
    NgForOf,
    NgIf,
    NgSwitch,
    NgSwitchCase,
    NgSwitchDefault,
} from '@angular/common';
import { ButtonDirective } from 'primeng/button';
import { DropdownModule } from 'primeng/dropdown';
import { InputSwitchModule } from 'primeng/inputswitch';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { OverlayPanelModule } from 'primeng/overlaypanel';
import { PrimeTemplate } from 'primeng/api';
import { TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';
import { interval, Subject, Subscription, switchMap, takeWhile } from 'rxjs';
import { DataService } from '../../../services/data/data.service';
import { FilterService } from '../../../components/filter/filter.service';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { BackendService } from 'src/app/services/backend/backend.service';
import {
    AlertTemplate,
    AlertTemplateStatus,
    Page,
    TableFilterParam,
    TemplateOverviewReq,
} from 'src/app/models/alert-rule.model';
import { UtilsService } from 'src/app/services/utils/util.service';
import { NotificationService } from 'src/app/services/notification/notification.service';
import { SELECTED_CUSTOMER, SITE_LIST } from 'src/app/config/constants';
import moment from 'moment-timezone';

@Component({
    selector: 'cbms-smart-alert-templates',
    standalone: true,
    imports: [
        ButtonDirective,
        DropdownModule,
        InputSwitchModule,
        MatIcon,
        MatProgressSpinner,
        NgForOf,
        NgIf,
        NgSwitchCase,
        OverlayPanelModule,
        PrimeTemplate,
        TableModule,
        TooltipModule,
        FormsModule,
        NgSwitch,
        NgSwitchDefault,
    ],
    templateUrl: './smart-alert-templates.component.html',
    styleUrl: './smart-alert-templates.component.scss',
})
export class SmartAlertTemplatesComponent implements OnInit, OnDestroy {
    readonly NAME = 'name';
    public cols: any[] = [
        { field: 'name', header: 'Template Name', position: 2, width: '100px' },
        {
            field: 'templateType',
            header: 'Template Type',
            position: 3,
            width: '100px',
        },
        {
            field: 'isEnabled',
            header: 'Activation Status',
            position: 4,
            width: '150px',
        },
    ];
    alertTemplateList: AlertTemplate[] = [];
    alertTempalteSubscription$ = new Subscription();
    alertTemplateStatus: Map<string, AlertTemplateStatus> = new Map();
    private pollingSubscriptions: Map<string, Subscription> = new Map();


    private customerId: string;
    customerSubscription = new Subscription();
    sitesSubscription = new Subscription();
    isLoading = false;
    firstIndex: number = 0;
    pageInfo: Page = {
        pageIndex: 0,
        pageSize: 30,
        totalElements: 0,
        sort: ''
    }

    constructor(
        private dataService: DataService,
        private filterService: FilterService,
        private router: Router,
        private activatedRoute: ActivatedRoute,
        private backendService: BackendService,
        private notificationService: NotificationService,
        private utilsService: UtilsService
    ) {}

    ngOnInit(): void {
        this.dataService.currentTabHasChanged('smartAlertTemplates');
        this.getSiteList();
        this.customerId = JSON.parse(sessionStorage.getItem(SELECTED_CUSTOMER))?.id;
        this.customerSubscription =
            this.filterService.customerFilterChanges$.subscribe((data) => {
                this.customerId = data.selectedCustomer?.id;
                this.getAlertTemplates();
                this.getSiteList();
            });
    }

    ngOnDestroy(): void {
        this.customerSubscription.unsubscribe();
        this.sitesSubscription.unsubscribe();
        this.alertTempalteSubscription$.unsubscribe();
        this.pollingSubscriptions.forEach((sub) => sub.unsubscribe());
    }


    addTemplate(): void {
        this.router.navigate(['add-smart-alert'], {
            relativeTo: this.activatedRoute,
        });
    }

    editTemplate(template: AlertTemplate): void {
        this.router.navigate([`edit-smart-alert`], {
            state: { templateId: template.id },
            relativeTo: this.activatedRoute,
        });
    }

    deleteSmartAlertTemplate(template: AlertTemplate): void {
        this.backendService
            .deleteAlertTemplate(template.id, this.customerId)
            .subscribe(() => {
                this.notificationService.addSuccessMessage(
                    'Smart Alert Template',
                    'Template successfully updated!'
                );
                this.getAlertTemplates();
            });
    }

    changeAlertTemplateStatus(template: AlertTemplate): void {
        this.backendService
            .changeAlertTemplateStatus(template.isEnabled, template.id)
            .subscribe(() => {
                this.notificationService.addSuccessMessage(
                    'Smart Alert Template',
                    'Template successfully updated!'
                );
            });
    }

    private getAlertTemplates(sortParam?: string): void {
        this.isLoading = true;
        const inputParam: TemplateOverviewReq = {
            customerId: this.customerId,
            pagination: {
                page: this.pageInfo.pageIndex,
                size: this.pageInfo.pageSize,
                sort: sortParam ? sortParam : this.NAME
            },
        };
        this.alertTempalteSubscription$ = this.backendService
            .getAlertTemplateList(inputParam)
            ?.subscribe((res) => {
                this.alertTemplateList = res.content;
                this.pageInfo.totalElements = res.totalElements;
                this.loadTemplatesGenerationStatus(res.content);
                this.isLoading = false;
            });
    }

    getSiteList() {
        this.sitesSubscription = this.backendService
            .getSitesByCustomer()
            .subscribe((response) => {
                const siteList = this.utilsService.orderListByFieldName(
                    response.content,
                    this.NAME
                );
                sessionStorage.setItem(SITE_LIST, JSON.stringify(siteList));
            });
    }

    getAlertTemplateStatus(
        id:string,
    ): AlertTemplateStatus | undefined {
        return this.alertTemplateStatus.get(id);
    }

    private pollTemplateStatus(alertTemplate: AlertTemplate, pollIntervalMs: number = 2000): void {
        const alertTemplateId = alertTemplate.id;
        this.pollingSubscriptions.get(alertTemplateId)?.unsubscribe();

        const sub = interval(pollIntervalMs).pipe(
            switchMap(() => this.backendService.getAlertTemplateGenerationStatus(alertTemplateId)),
            takeWhile((status: AlertTemplateStatus) => status.status === 'IN_PROGRESS', true)
        ).subscribe((status: AlertTemplateStatus) => {
            const previousStatus = this.alertTemplateStatus.get(alertTemplateId)?.status || 'COMPLETED';
            this.alertTemplateStatus.set(alertTemplateId, status);

            // If polling is done, clean up subscription
            if (status.status !== 'IN_PROGRESS') {
                this.pollingSubscriptions.get(alertTemplateId)?.unsubscribe();
                this.pollingSubscriptions.delete(alertTemplateId);

                const now = moment(new Date()).tz('UTC');
                const endDate = moment(status.endDate).tz('UTC');
                const durationInSeconds = moment.duration(now.diff(endDate)).asSeconds();
                if (previousStatus === 'IN_PROGRESS' || durationInSeconds < 30) {
                    if (status.status === 'COMPLETED') {
                        this.notificationService.addSuccessMessage(
                            'Smart Alert Template',
                            `All Rules generated - ${alertTemplate.name}`
                        );
                    } else if (status.status === 'FAILED') {
                        this.notificationService.addErrorMessage(
                            'Smart Alert Template',
                            `Failed to generate rules - ${alertTemplate.name}`
                        );
                    }
                }
            }
        });

        this.pollingSubscriptions.set(alertTemplateId, sub);
    }

    private loadTemplatesGenerationStatus(alertTemplates: AlertTemplate[]): void {
        if (!alertTemplates || alertTemplates.length === 0) {
            return;
        }
        this.alertTemplateStatus.clear();
        alertTemplates.forEach(alertTemplate => {
            this.pollTemplateStatus(alertTemplate);
        });
    }

    loadTemplateListLazily(event: TableFilterParam) {
        if (this.customerId) {
            this.firstIndex = event.first;
            this.pageInfo.pageIndex = event.first / this.pageInfo.pageSize;
            this.pageInfo.sort = event.sortField;
            if (event.sortField) {
                const sortParam = event.sortField.concat(`,${event.sortOrder === 1 ? 'asc' : 'desc'}`);
                this.getAlertTemplates(sortParam);
                return;
            }
            this.getAlertTemplates();
        }
    }
}
